import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Flex, Form, GetProp, notification, Spin, UploadProps } from 'antd'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  applySkipRowsRange,
  applySkipRowsRangeToText,
  generateValidationSchema,
  getKeptColumnIndicesForRawData,
  LIMIT_FILE_SIZE,
  formatSheetData,
  defaultValues,
  EXTRA_COLUMN,
  findFirstHeaderRowIndex,
  preprocessSheetLikeDataForPreview,
} from '../add-file-mapping/utils'
import { yupResolver } from '@hookform/resolvers/yup'
import { SYSTEM_MESSAGE_IDS } from 'src/app/shared/constants/systemMessages.constants'
import * as XLSX from 'xlsx'
import { pickFirstViableExcelSheet } from '../pickFirstViableExcelSheet'

import MappingFields from './MappingFields'
import UploadForm from './UploadForm'
import './index.scss'
import {
  checkDuplicateFileMappingFormats,
  createFileMappingFormats,
  getFileMappingFormat,
} from 'src/app/shared/services/ClientSetupService'
import { useNavigate, useParams } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { FILE_TYPE_MAPPING } from 'src/app/shared/constants/app.constants'
import AdditionalChangedModal from './AdditionalChangeModal'
import { useSelector } from 'react-redux'
import { getLookupValues } from 'src/app/store/masterSetup'
import { Value } from 'src/app/shared/models/lookupValues.model'
import { csvHelper } from '../csvHelper'
import RichTextField from 'src/app/shared/components/common/rich-text-field'
import MappingFieldsTxt from '../add-file-mapping/MappingFieldsTxt'
import { setDateFormat } from '../view-file-mapping/utils'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]
interface Props {
  children: React.ReactNode
  setFileContainsHeaderRow: (value: any) => void
  setFileData: (value: any) => void
  setColumnDefs: (value: any) => void
  setIsTxtFile: (value: any) => void
  setLoading: (value: any) => void
}
export const mappingFields = [
  'checkNumber',
  'amount',
  'issuedDate',
  'accountNumber',
  'issuedPayee',
  'issuedDateFormat',
  'dateInFileDontIncludeSeparators',
]

function CopyFileMapping(props: Props) {
  const params = useParams()
  const { id } = params
  const { t } = useTranslation()
  const navigate = useNavigate()
  const lookupValues = useSelector(getLookupValues)
  const { FormatType, DateFormat, DateFormatWithSeparator } = lookupValues || {}
  const [viewNewUploadError, setViewNewUploadError] = useState(false)
  const [columnList, setColumnList] = useState<any>()
  const [isFetched, setIsFetched] = useState(false)
  const [isFirstTime, setIsFirstTime] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  const [file, setFile] = useState<any>()
  const [fileData, setFileData] = useState<any>()
  const [showChangedValidModal, setShowChangedValidModal] = useState(false)
  const originalFormatName = useRef(null)
  const originalData = useRef(null)
  const btnSaveRef = useRef<any>(null)
  const detectedHeaderRowSkipRef = useRef(0)
  // sequential position in filtered fileData -> original 0-based column index in the file.
  const keptColumnIndicesRef = useRef<number[]>([])

  const [selectedColumns, setSelectedColumns] = useState<any>({
    checkNumber: null,
    amount: null,
    issuedDate: null,
    accountNumber: null,
    issuedPayee: null,
  })

  const getDetailFileMapping = async () => {
    if (id) {
      const res = await getFileMappingFormat(id)

      const checkNumberList = res?.data?.checkNumberRange?.split('-')
      const amountList = res?.data?.amountRange?.split('-')
      const issuedDateList = res?.data?.issuedDateRange?.split('-')
      const accountNumberList = res?.data?.accountNumberRange?.split('-')
      const issuedPayeeList = res?.data?.issuedPayeeRange?.split('-')

      if (
        res?.data?.sampleFilePath &&
        res?.data?.fileContainsHeaderRow !== undefined
      )
        await readFileDataFromDB(
          res?.data?.sampleFilePath,
          0,
          0,
          res?.data?.fileContainsHeaderRow,
          res?.data?.sampleFile,
          res?.data?.formatType,
          res?.data?.textQualifier,
          res?.data?.skipRowsFrom,
          res?.data?.skipRowsTo,
        )

      originalFormatName.current = res?.data?.formatName
      const formattedData = {
        ...res?.data,
        formatName: '',
        skipRowsEnabled: res?.data?.skipRowsFrom != null && res?.data?.skipRowsTo != null,
        dateInFileDontIncludeSeparators:
          typeof res?.data?.issuedDateFormat === 'string' &&
          (res?.data?.issuedDateFormat?.includes('/') || res?.data?.issuedDateFormat?.includes('-')),
        checkNumberFrom: checkNumberList?.[0],
        checkNumberTo: checkNumberList?.[1],
        amountFrom: amountList?.[0],
        amountTo: amountList?.[1],
        issuedDateFrom: issuedDateList?.[0],
        issuedDateTo: issuedDateList?.[1],
        accountNumberFrom: accountNumberList?.[0],
        accountNumberTo: accountNumberList?.[1],
        issuedPayeeFrom: issuedPayeeList?.[0],
        issuedPayeeTo: issuedPayeeList?.[1],
        accountNumber: res?.data?.formatType === 'txt' ? null : res?.data?.accountNumber,
        amount: res?.data?.formatType === 'txt' ? null : res?.data?.amount,
        checkNumber: res?.data?.formatType === 'txt' ? null : res?.data?.checkNumber,
        issuedDate: res?.data?.formatType === 'txt' ? null : res?.data?.issuedDate,
        issuedPayee: res?.data?.formatType === 'txt' ? null : res?.data?.issuedPayee,
        issuedDateFormat: setDateFormat(
          res?.data?.issuedDateFormat,
          (typeof res?.data?.issuedDateFormat === 'string' && res?.data?.issuedDateFormat?.includes('/')) ||
            res?.data?.issuedDateFormat?.includes('-')
            ? DateFormatWithSeparator
            : DateFormat,
        ),
      }
      setIsFetched(true)
      setSelectedColumns({
        accountNumber: res?.data?.accountNumberColumn >= 0 ? res?.data?.accountNumberColumn : null,
        amount: res?.data?.amountColumn >= 0 ? res?.data?.amountColumn : null,
        checkNumber: res?.data?.checkNumberColumn >= 0 ? res?.data?.checkNumberColumn : null,
        issuedDate: res?.data?.issuedDateColumn >= 0 ? res?.data?.issuedDateColumn : null,
        issuedPayee: res?.data?.issuedPayeeColumn >= 0 ? res?.data?.issuedPayeeColumn : null,
      })
      originalData.current = formattedData

      return formattedData
    }
  }
  const {
    control,
    formState: { errors, dirtyFields, touchedFields },
    setError,
    clearErrors,
    setValue,
    resetField,
    setFocus,
    getValues,
    handleSubmit,
    trigger,
  } = useForm<any>({
    mode: 'all',
    defaultValues: async () => await getDetailFileMapping(),
    resolver: yupResolver(generateValidationSchema(t, 1, fileData?.[0]?.length)),
  })
  const formatType = useWatch({
    control,
    name: 'formatType',
  })

  const fileContainsIssuedDate = useWatch({
    control,
    name: 'fileContainsIssuedDate',
  })

  const fileContainsHeaderRow = useWatch({
    control,
    name: 'fileContainsHeaderRow',
  })

  const textQualifier = useWatch({
    control,
    name: 'textQualifier',
  })

  const dateInFileDontIncludeSeparators = useWatch({
    control,
    name: 'dateInFileDontIncludeSeparators',
  })

  const isTxt = useMemo(() => formatType === 'txt', [formatType])
  const isCsvFile = useMemo(() => formatType === 'csv', [formatType])

  const readUploadFileData = (file: FileType) => {
    const reader = new FileReader()

    reader.onload = (e: any) => {
      let converted = e.target.result
      let headerColumns: any = []
      if (isCsvFile || isTxt) {
        // US32690 - the Row Skip Range is applied to the raw file text, before the existing parsing
        // and preamble detection, so the remaining rows keep going through the current pipeline.
        converted = applySkipRowsRangeToText(converted, getValues('skipRowsFrom'), getValues('skipRowsTo'))
        if (isCsvFile && csvHelper(converted, getValues('textQualifier')) === false) {
          setError('sampleFile', {
            message: t(`SystemMessages:${getValues('textQualifier') == 'SINGLEQUOTE' ? 'PPC09' : 'PPC10'}`),
          })
          setFile(null)
          setViewNewUploadError(true)
          props?.setLoading(false)
          return false
        } else {
          converted = isCsvFile
            ? csvHelper(converted, getValues('textQualifier'))
            : converted.split(/\r\n|\n|\r/gm).map((item: any) => item.split(''))

          if (isCsvFile) {
            detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(converted as unknown[][], fileContainsHeaderRow)
            keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
              converted as unknown[][],
              fileContainsHeaderRow,
              false,
            )
            converted = preprocessSheetLikeDataForPreview(converted, fileContainsHeaderRow, undefined, false)
          } else {
            detectedHeaderRowSkipRef.current = 0
            keptColumnIndicesRef.current = []
          }

          const dataSkipped =
            0 < converted?.length
              ? converted?.[0]
              : []

          const maxLength = Math.max(...converted.map((row: any) => row.length))
          isTxt && dataSkipped.push(...Array(EXTRA_COLUMN + maxLength - dataSkipped?.length).fill(''))

          headerColumns = dataSkipped.map((item: any, index: any) =>
            isTxt ? index + 1 : item.trim().replace(/[\n\r]*/g, ''),
          )

          const columnList = headerColumns?.map((item: any, seqIdx: any) => {
            const originalIdx = isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx
            return {
              value: originalIdx,
              label: fileContainsHeaderRow
                ? String(item)?.includes('_blank_')
                  ? ``
                  : `${isTxt ? originalIdx + 1 : item}`
                : `${isTxt ? originalIdx + 1 : `Column ${originalIdx + 1}`}`,
            }
          })

          if (headerColumns) {
            setColumnList(columnList)
          }
          const rowData = converted?.slice(1)

          const newRowData: any = []
          rowData.forEach((row: any) => {
            let newRow = {}
            row.forEach((cell: any, cellIndex: number) => {
              if (cell === '' && cellIndex === 0) {
                row[cellIndex] = ''
              } else {
                newRow = {
                  ...newRow,
                  [headerColumns[cellIndex]]: cell,
                }
              }
            })

            newRowData.push(newRow)
          })
        }

        converted = converted?.slice(
          0,
          converted?.length,
        )

        if (isTxt && fileContainsHeaderRow) {
          converted.unshift(headerColumns)
        }

        setFileData(converted)
        props?.setLoading(false)
        return false
      }

      const binaryStr = e.target.result

      const workbook = XLSX.read(binaryStr, {
        type: 'binary',
        cellDates: !isCsvFile,
        raw: isCsvFile,
      })

      const { worksheet } = pickFirstViableExcelSheet(workbook, { raw: isCsvFile })

      // Convert sheet data to JSON
      const listData = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        blankrows: true,
        range: 0,
        defval: '',
        raw: isCsvFile,
      })

      const sheetData: any = applySkipRowsRange(
        listData as unknown[][],
        getValues('skipRowsFrom'),
        getValues('skipRowsTo'),
      )

      detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(sheetData as unknown[][], fileContainsHeaderRow)
      keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
        sheetData as unknown[][],
        fileContainsHeaderRow,
        false,
      )
      const formattedSheetData = formatSheetData(sheetData, fileContainsHeaderRow)
      setFileData(formattedSheetData)

      const columnList = formattedSheetData?.[0]?.map((item: any, seqIdx: any) => {
        const originalIdx = keptColumnIndicesRef.current[seqIdx] ?? seqIdx
        return {
          value: originalIdx,
          label: fileContainsHeaderRow
            ? String(item)?.includes('_blank_')
              ? ``
              : item
            : `Column ${originalIdx + 1}`,
          key: fileContainsHeaderRow ? `${item}-${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        }
      })

      setColumnList(columnList)
    }

    if (isTxt || isCsvFile) {
      reader.readAsText(file, 'UTF-8')
    } else {
      reader.readAsBinaryString(file)
    }
    reader.onloadend = () => {
      props?.setLoading(false)
    }
  }

  const readFileDataFromDB = useCallback(
    async (
      url: string,
      rowsToSkipAtStart: number,
      rowsToSkipAtEnd: number,
      fileContainsHeaderRow: boolean,
      sampleFile: string,
      formatTypeInput: string,
      textQualifier: string,
      skipRowsFrom?: number | null,
      skipRowsTo?: number | null,
    ) => {
      const isCsvFile = formatTypeInput === 'csv'
      const isTxt = formatTypeInput === 'txt'
      try {
        const data = await (await fetch(url)).arrayBuffer()
        const dataBlob = new Blob([new Uint8Array(data)])
        const fileFromDB = new File([dataBlob], sampleFile, isTxt ? { type: 'text/plain;charset=utf-8' } : {})
        const dataText = await fileFromDB.text()

        // US32690 - the Row Skip Range is applied to the raw file text, before the existing parsing
        // and preamble detection, so the remaining rows keep going through the current pipeline.
        let converted: any = applySkipRowsRangeToText(dataText, skipRowsFrom, skipRowsTo)
        let headerColumns: any = []
        if (isCsvFile || isTxt) {
          if (isCsvFile && csvHelper(converted, textQualifier) === false) {
            setError('sampleFile', {
              message: t(`SystemMessages:${textQualifier == 'SINGLEQUOTE' ? 'PPC09' : 'PPC10'}`),
            })
            setFile(null)

            return
          } else {
            converted = isCsvFile
              ? csvHelper(converted, textQualifier)
              : converted.split(/\r\n|\n|\r/gm).map((item: any) => item.split(''))

            if (isCsvFile) {
              detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(converted as unknown[][], fileContainsHeaderRow)
              keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
                converted as unknown[][],
                fileContainsHeaderRow,
                Number(rowsToSkipAtStart) > 0,
              )
            } else {
              detectedHeaderRowSkipRef.current = 0
              keptColumnIndicesRef.current = []
            }

            const dataSkipped =
              Number(rowsToSkipAtStart) + Number(rowsToSkipAtEnd) < converted?.length
                ? converted?.[Number(rowsToSkipAtStart ?? 0)]
                : []

            const maxLength = Math.max(...converted.map((row: any) => row.length))
            isTxt && dataSkipped.push(...Array(EXTRA_COLUMN + maxLength - dataSkipped?.length).fill(''))

            headerColumns = dataSkipped.map((item: any, index: any) =>
              isTxt ? index + 1 : item.trim().replace(/[\n\r]*/g, ''),
            )

            // headerColumns here is the RAW row (N items). For CSV we want only the kept columns;
            // for TXT we keep the full sequential layout.
            const columnList = isTxt
              ? headerColumns?.map((_item: any, idx: number) => ({
                  value: idx,
                  label: `${idx + 1}`,
                }))
              : keptColumnIndicesRef.current.map((originalIdx: number) => {
                  const cell = headerColumns?.[originalIdx]
                  return {
                    value: originalIdx,
                    label: fileContainsHeaderRow
                      ? String(cell ?? '').includes('_blank_')
                        ? ``
                        : cell
                      : `Column ${originalIdx + 1}`,
                  }
                })

            if (headerColumns) {
              setColumnList(columnList)
            }
            const rowData = converted?.slice(1)

            const newRowData: any = []
            rowData.forEach((row: any) => {
              let newRow = {}
              row.forEach((cell: any, cellIndex: number) => {
                if (cell === '') {
                  row[cellIndex] = ''
                } else {
                  newRow = {
                    ...newRow,
                    [headerColumns[cellIndex]]: cell,
                  }
                }
              })

              newRowData.push(newRow)
            })
          }

          converted = converted?.slice(
            Number(rowsToSkipAtStart),
            converted?.length > Number(rowsToSkipAtEnd) ? converted?.length - Number(rowsToSkipAtEnd) : 0,
          )

          if (isTxt && fileContainsHeaderRow) {
            converted.unshift(headerColumns)
          }

          if (isCsvFile) {
            converted = preprocessSheetLikeDataForPreview(
              converted,
              fileContainsHeaderRow,
              undefined,
              Number(rowsToSkipAtStart) > 0,
            )
          }

          setFile(fileFromDB)
          setFileData(converted)
          return
        }

        const workbook = XLSX.read(data, {
          cellDates: !isCsvFile,
          raw: isCsvFile,
        })

        const { worksheet } = pickFirstViableExcelSheet(workbook, { raw: isCsvFile })

        // Convert sheet data to JSON
        const listData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          blankrows: true,
          range: 0,
          defval: '',
          raw: isCsvFile,
        })
        const rawSheetData = applySkipRowsRange(listData as unknown[][], skipRowsFrom, skipRowsTo)
        detectedHeaderRowSkipRef.current = findFirstHeaderRowIndex(rawSheetData, fileContainsHeaderRow)
        let sheetData: any = rawSheetData?.slice(
          Number(rowsToSkipAtStart),
          rawSheetData?.length - Number(rowsToSkipAtEnd),
        )

        keptColumnIndicesRef.current = getKeptColumnIndicesForRawData(
          sheetData as unknown[][],
          fileContainsHeaderRow,
          Number(rowsToSkipAtStart) > 0,
        )

        setFile(fileFromDB)
        const formattedSheetData = formatSheetData(
          sheetData,
          fileContainsHeaderRow,
          undefined,
          Number(rowsToSkipAtStart) > 0,
        )
        setFileData(formattedSheetData)

        const columnList = formattedSheetData?.[0]?.map((item: any, seqIdx: any) => {
          const originalIdx = keptColumnIndicesRef.current[seqIdx] ?? seqIdx
          return {
            value: originalIdx,
            label: fileContainsHeaderRow
              ? String(item)?.includes('_blank_')
                ? ``
                : item
              : `Column ${originalIdx + 1}`,
            key: fileContainsHeaderRow ? `${item}-${originalIdx + 1}` : `Column ${originalIdx + 1}`,
          }
        })

        setColumnList(columnList)
      } catch (err: any) {
        //Handle error file
      }
    },
    [],
  )

  const clearMappingFieldsError = useCallback(() => {
    clearErrors('checkNumber')
    clearErrors('amount')
    clearErrors('issuedDate')

    clearErrors('checkNumberFrom')
    clearErrors('amountFrom')
    clearErrors('issuedDateFrom')
    clearErrors('accountNumberFrom')
    clearErrors('issuedPayeeFrom')

    clearErrors('checkNumberTo')
    clearErrors('amountTo')
    clearErrors('issuedDateTo')
    clearErrors('accountNumberTo')
    clearErrors('issuedPayeeTo')

    clearErrors('issuedDateFormat')
    clearErrors('dateInFileDontIncludeSeparators')
  }, [clearErrors])

  const resetMappingFieldsValue = useCallback(() => {
    setSelectedColumns({
      checkNumber: null,
      amount: null,
      issuedDate: null,
      accountNumber: null,
      issuedPayee: null,
    })
    resetField('dateInFileDontIncludeSeparators', { defaultValue: false })
    resetField('issuedDateFormat', { defaultValue: null })

    resetField('checkNumber', { defaultValue: null })
    resetField('amount', { defaultValue: null })
    resetField('issuedDate', { defaultValue: null })
    resetField('accountNumber', { defaultValue: null })
    resetField('issuedPayee', { defaultValue: null })

    resetField('checkNumberFrom', { defaultValue: null })
    resetField('checkNumberTo', { defaultValue: null })
    resetField('amountFrom', { defaultValue: null })
    resetField('amountTo', { defaultValue: null })
    resetField('issuedDateFrom', { defaultValue: null })
    resetField('issuedDateTo', { defaultValue: null })
    resetField('accountNumberFrom', { defaultValue: null })
    resetField('accountNumberTo', { defaultValue: null })
    resetField('issuedPayeeFrom', { defaultValue: null })
    resetField('issuedPayeeTo', { defaultValue: null })

    clearMappingFieldsError()
  }, [setValue])

  const getCurrentFormatType = (formatType: string) => {
    return FormatType?.find((formatItem: Value) => formatItem.value === formatType)?.label
  }

  const beforeUpload = (file: FileType) => {
    // return false to prevent default action ( call upload api ) of antd upload
    props?.setLoading(true)
    clearErrors('sampleFile')
    clearMappingFieldsError()
    setViewNewUploadError(false)
    if (!formatType) {
      setError('sampleFile', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.SELECT_FORMAT_TYPE_FIRST) })
      setViewNewUploadError(true)
      return false
    }
    if (file?.size > LIMIT_FILE_SIZE) {
      setError('sampleFile', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.EXCEED_LIMIT_FILE_SIZE) })
      setViewNewUploadError(true)
      return false
    }
    const fileType = file?.type
    const selectedFormatType = formatType as string
    if (!FILE_TYPE_MAPPING[selectedFormatType].includes(fileType)) {
      setError('sampleFile', {
        message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.INVALID_FILE_FORMAT, {
          Format_Type: getCurrentFormatType(selectedFormatType),
        }),
      })
      setViewNewUploadError(true)
      return false
    }
    // UPLOAD VALID FILE
    setFile(file)
    setValue('sampleFile', file?.name)
    resetMappingFieldsValue()

    readUploadFileData(file)
    return false
  }

  useEffect(() => {
    setFocus('formatName')
  }, [setFocus])

  const resetDateFormat = () => {
    setValue('issuedDateFormat', null)
    clearErrors('issuedDateFormat')
    trigger('issuedDateFormat')
  }

  const columnDefs = useMemo(() => {
    const resolveOriginalIdx = (seqIdx: number) =>
      isTxt ? seqIdx : keptColumnIndicesRef.current[seqIdx] ?? seqIdx

    if (fileContainsHeaderRow && fileData)
      return fileData?.[0]?.map((item: any, seqIdx: any) => {
        const originalIdx = resolveOriginalIdx(seqIdx)
        return {
          colId: originalIdx,
          field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
          headerName: String(item)?.includes('_blank_') ? '' : String(item),
          minWidth: 40,
          sortable: false,
        }
      })
    return fileData?.[0]?.map((item: any, seqIdx: any) => {
      const originalIdx = resolveOriginalIdx(seqIdx)
      return {
        colId: originalIdx,
        field: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        headerName: isTxt ? `${originalIdx + 1}` : `Column ${originalIdx + 1}`,
        minWidth: 40,
        sortable: false,
      }
    })
  }, [fileContainsHeaderRow, fileData])

  useEffect(() => {
    props.setColumnDefs([])
    props.setFileData([])
    props.setFileContainsHeaderRow([])
    props.setIsTxtFile(isTxt)
  }, [isTxt])

  useEffect(() => {
    props.setColumnDefs(columnDefs)
    props.setFileData(fileData)
    props.setFileContainsHeaderRow(fileContainsHeaderRow)
  }, [fileContainsHeaderRow, fileData, columnDefs])

  // US32690 - the Row Skip Range changes which rows reach the preview and the column list,
  // so the already selected sample file is read again instead of being cleared.
  const handleSkipRowsChange = useCallback(() => {
    if (!file?.name) return

    props?.setLoading(true)
    resetMappingFieldsValue()
    readUploadFileData(file)
  }, [file, resetMappingFieldsValue])

  const resetUploadFile = useCallback(() => {
    detectedHeaderRowSkipRef.current = 0
    setColumnList([])
    setFile({})
    setValue('sampleFile', '')
    resetMappingFieldsValue()
  }, [resetField, setFile, resetMappingFieldsValue])

  useEffect(() => {
    if (
      formatType ||
      fileContainsHeaderRow ||
      fileContainsIssuedDate ||
      textQualifier
    ) {
      if (isFirstTime) {
        setIsFirstTime(false)
      }
    }
  }, [
    formatType,
    resetUploadFile,
    fileContainsHeaderRow,
    fileContainsIssuedDate,
    isFirstTime,
    textQualifier,
  ])

  const requiredFields = !isTxt
    ? getValues('checkNumber') == null ||
      getValues('amount') == null ||
      (fileContainsIssuedDate && getValues('issuedDate') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateFormat') === null)
    : getValues('checkNumberFrom') == null ||
      getValues('checkNumberTo') == null ||
      getValues('amountFrom') == null ||
      getValues('amountTo') == null ||
      (fileContainsIssuedDate && getValues('issuedDateFrom') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateTo') == null) ||
      (fileContainsIssuedDate && getValues('issuedDateFormat') === null)

  const isSaveDisabled =
    !!Object.keys(errors)?.length ||
    getValues('sampleFile') === '' ||
    requiredFields ||
    !getValues('formatName')?.trim()

  const checkDuplicate = async (name: string) => {
    const res = await checkDuplicateFileMappingFormats(name)
    return res?.data
  }

  const handleCloseAdditionalModal = () => {
    setShowChangedValidModal(false)
  }

  const getValueSelection = (label: any) => {
    return columnList?.find((item: any) => item?.label === label)?.value
  }

  const countDifferentObject = (originalData: any, newData: any) => {
    let countDifferent = 0
    const listKeys = Object.keys(newData)
    for (let i = 0; i < listKeys.length; i++) {
      const keyItem: string = listKeys[i]
      if (originalData[keyItem]?.toString() !== newData[keyItem]?.toString()) {
        countDifferent++
        break
      }
    }

    return countDifferent === 0
  }

  const onSubmit = async (rawData: any) => {
    const data: { [key: string]: any } = {
      id: rawData?.id,
      accountNumber: rawData?.accountNumber,
      accountNumberRange:
        rawData.accountNumberFrom && rawData.accountNumberTo
          ? `${rawData.accountNumberFrom}-${rawData.accountNumberTo}`
          : '',

      amount: isTxt ? '0' : rawData?.amount,
      amountRange: rawData.amountFrom && rawData.amountTo ? `${rawData.amountFrom}-${rawData.amountTo}` : '',

      checkNumber: isTxt ? '0' : rawData?.checkNumber,
      checkNumberRange:
        rawData.checkNumberFrom && rawData.checkNumberTo ? `${rawData.checkNumberFrom}-${rawData.checkNumberTo}` : '',

      issuedDate: isTxt ? '0' : rawData?.issuedDate,
      issuedDateRange:
        rawData.issuedDateFrom && rawData.issuedDateTo ? `${rawData.issuedDateFrom}-${rawData.issuedDateTo}` : '',

      issuedPayee: rawData?.issuedPayee,
      issuedPayeeRange:
        rawData.issuedPayeeFrom && rawData.issuedPayeeTo ? `${rawData.issuedPayeeFrom}-${rawData.issuedPayeeTo}` : '',

      dateInFileDontIncludeSeparators: rawData.dateInFileDontIncludeSeparators,
      fileContainsHeaderRow: rawData?.fileContainsHeaderRow,
      fileContainsIssuedDate: rawData?.fileContainsIssuedDate,
      formatName: rawData?.formatName,
      formatType: rawData?.formatType,
      rowsToSkipAtEnd: 0,
      rowsToSkipAtStart: 0,
      skipRowsFrom: rawData?.skipRowsFrom,
      skipRowsTo: rawData?.skipRowsTo,
      sampleFile: rawData?.sampleFile,
      textQualifier: rawData?.textQualifier,
      issuedDateFormat: rawData?.issuedDateFormat,
    }
    if (btnSaveRef.current) {
      btnSaveRef.current.disabled = true
    }
    const isDuplicated = await checkDuplicate(data?.formatName)

    if (isDuplicated) {
      setError('formatName', { message: t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FORMAT_NAME_UNIQUE) })
      return
    }

    setIsLoading(true)
    const { formatName, ...originalFormat } = originalData?.current as any
    const { formatName: newFormatName, ...newFormatData } = rawData

    //GBBM-562 - Remove check different object for copy file mapping
    // if (countDifferentObject(originalFormat, newFormatData)) {
    //   setShowChangedValidModal(true)
    //   setIsLoading(false)
    //   if (btnSaveRef.current) {
    //     btnSaveRef.current.disabled = false
    //   }
    //   return false
    // }

    const formData: any = new FormData()

    const generateColumnNumber = (field: string) => {
      // columnList.value is the original 0-based column index in the file.
      // getValueSelection maps the form's label-based value back to that index.
      const v = getValueSelection(getValues(field))
      if (v === null || v === undefined) return null
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    }

    Object.keys(data).forEach((key: any) => {
      if (key === 'sampleFile') formData.append(key, file)
      else if (
        ['checkNumber', 'amount', 'issuedDate', 'accountNumber', 'issuedPayee']?.includes(key) &&
        (getValues(key) !== null || isTxt) &&
        getValues(key) !== undefined
      ) {
        formData.append(key, isTxt ? '0' : data[key])
      } else if (
        ['issuedPayeeRange', 'issuedDateRange', 'checkNumberRange', 'amountRange', 'accountNumberRange']?.includes(
          key,
        ) &&
        (getValues(key) !== null || isTxt) &&
        getValues(key) !== undefined
      ) {
        formData.append(key, data[key])
      } else if (key !== 'dateInFileDontIncludeSeparators' && getValues(key) !== null && getValues(key) !== undefined)
        formData.append(key, data[key as keyof typeof data])
    })

    formData?.delete('checkNumberColumn')
    formData?.delete('amountColumn')
    formData?.delete('issuedDateColumn')
    formData?.delete('accountNumberColumn')
    formData?.delete('issuedPayeeColumn')

    if (!isTxt) {
      formData?.append('checkNumberColumn', generateColumnNumber('checkNumber'))
      formData?.append('amountColumn', generateColumnNumber('amount'))
      if (generateColumnNumber('issuedDate') !== null) {
        formData?.append('IssuedDateColumn', generateColumnNumber('issuedDate'))
      }

      if (generateColumnNumber('accountNumber') !== null) {
        formData?.append('accountNumberColumn', generateColumnNumber('accountNumber'))
      }

      if (generateColumnNumber('issuedPayee') !== null) {
        formData?.append('IssuedPayeeColumn', generateColumnNumber('issuedPayee'))
      }
    } else {
      formData?.append('checkNumberColumn', '0')
      formData?.append('amountColumn', '0')

      formData?.append('IssuedDateColumn', '0')
      formData?.append('accountNumberColumn', '0')
      formData?.append('IssuedPayeeColumn', '0')
    }
    try {
      const res = await createFileMappingFormats(formData)
      if (res?.statusCode === 'Success') {
        navigate(menuUrls.fileMappingSetup)

        notification.success({
          message: (
            <RichTextField
              richText={t('SystemMessages:' + SYSTEM_MESSAGE_IDS.FILE_MAPPING_CREATED_SUCCESSFULLY, {
                formatName: getValues('formatName'),
              })}
            />
          ),

          closeIcon: null,
        })
      }

      if (btnSaveRef.current) {
        btnSaveRef.current.disabled = false
      }
    } catch (err: any) {
      console.log('err ', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClickCancel = () => {
    navigate(menuUrls.fileMappingSetup)
  }

  const checkEnableResetBtn = () => {
    return mappingFields.some((field: string) => getValues(field))
  }

  const [form] = Form.useForm()

  useEffect(() => {
    if (['csv'].includes(formatType) && !getValues('sampleFile')) {
      setValue('textQualifier', lookupValues?.TextQualifier[0]?.value)
    } else if (!getValues('sampleFile')) {
      setValue('textQualifier', null)
    }
  }, [formatType])

  return (
    <Form
      form={form}
      name='file-mapping'
      className='file-mapping-setup-form-container'
      layout='vertical'
      onFinish={handleSubmit(onSubmit)}
    >
      {isFetched ? (
        <>
          <Flex className='file-mapping-setup-form-wrapper' style={{ flexDirection: 'column' }}>
            <UploadForm
              errors={errors}
              control={control}
              setValue={setValue}
              trigger={trigger}
              clearErrors={clearErrors}
              onSkipRowsChange={handleSkipRowsChange}
              beforeUpload={beforeUpload}
              file={file}
              formatType={formatType}
              resetUploadFile={resetUploadFile}
            />
            {!viewNewUploadError && (
              <>
                {(file?.name || getValues('sampleFile')) && (
                  <>
                    {props.children}
                    {isTxt ? (
                      <MappingFieldsTxt
                        errors={errors}
                        control={control}
                        dirtyFields={dirtyFields}
                        fileContainsIssuedDate={fileContainsIssuedDate}
                        dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                        trigger={trigger}
                        getValues={getValues}
                        resetMappingFields={resetMappingFieldsValue}
                        resetDateFormat={resetDateFormat}
                        enableResetBtn={checkEnableResetBtn()}
                      />
                    ) : (
                      <MappingFields
                        errors={errors}
                        control={control}
                        dirtyFields={dirtyFields}
                        fileContainsIssuedDate={fileContainsIssuedDate}
                        dateInFileDontIncludeSeparators={dateInFileDontIncludeSeparators}
                        columnList={columnList}
                        getValues={getValues}
                        resetMappingFields={resetMappingFieldsValue}
                        selectedColumns={selectedColumns}
                        setSelectedColumns={setSelectedColumns}
                        enableResetBtn={checkEnableResetBtn()}
                        resetDateFormat={resetDateFormat}
                      />
                    )}
                  </>
                )}
              </>
            )}
          </Flex>

          <div className='button-container'>
            <Flex className='button-group' justify='flex-end' gap={24}>
              <Button className='cancel-button' onClick={handleClickCancel}>
                {t('OverallFunctionality:cancel_btn')}
              </Button>

              <Button
                ref={btnSaveRef}
                className='save-button'
                type='primary'
                disabled={isSaveDisabled}
                htmlType='button'
                onClick={form.submit}
                loading={isLoading}
              >
                {t('OverallFunctionality:save_changes_btn')}
              </Button>
            </Flex>
          </div>
          <AdditionalChangedModal
            isOpen={showChangedValidModal}
            onClose={handleCloseAdditionalModal}
            formatName={originalFormatName.current ?? ''}
          />
        </>
      ) : (
        <div className='loading-wrapper'>
          <Spin />
        </div>
      )}
    </Form>
  )
}

export default CopyFileMapping

